import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BriefcaseBusiness, FileSignature, Link2, ReceiptText, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (value, currency = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(Number(value || 0));

export function CustomerRelationshipsPanel({ customerId }) {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState([]);
  const [contractId, setContractId] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [overview, contracts] = await Promise.all([
        axios.get(`${API}/crossstudio-links/customers/${customerId}/overview`),
        axios.get(`${API}/crossstudio-links/customers/${customerId}/linkable-contracts`),
      ]);
      setData(overview.data);
      setChoices(contracts.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Verknüpfte Daten konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [customerId]);

  const link = async () => {
    if (!contractId) return toast.error("Bitte einen Vertrag auswählen");
    try {
      await axios.post(`${API}/crossstudio-links/customers/${customerId}/contracts/${contractId}/link`, {});
      setContractId("");
      toast.success("Vertrag mit dem Kunden verknüpft");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verknüpfung fehlgeschlagen"); }
  };

  const unlink = async (id) => {
    if (!window.confirm("Vertragsverknüpfung lösen? Der Vertrag selbst bleibt erhalten.")) return;
    try {
      await axios.post(`${API}/crossstudio-links/customers/${customerId}/contracts/${id}/unlink`);
      toast.success("Verknüpfung gelöst");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verknüpfung konnte nicht gelöst werden"); }
  };

  const contracts = data?.contracts || [];
  const projects = data?.projects || [];
  const costs = data?.incoming_invoices || [];
  const unlinked = choices.filter((item) => !item.customer_id);

  return <Card className="p-5 border-[var(--brand-border)]" data-testid="customer-relationships-panel">
    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
      <div><h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Link2 className="w-4 h-4 text-[var(--brand-primary)]" /> Verknüpfte Geschäftsdaten</h3><p className="text-xs text-[var(--brand-muted)] mt-1">Verträge, Projekte und Kosten bleiben in ihren jeweiligen Studios gespeichert und werden hier nur angezeigt.</p></div>
      <Button size="sm" variant="outline" disabled={loading} onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktualisieren</Button>
    </div>
    <div className="grid md:grid-cols-3 gap-3">
      <div className="border border-[var(--brand-border-light)] rounded-lg p-3"><div className="text-xs font-semibold text-[var(--brand-ink)] flex items-center gap-1"><FileSignature className="w-3.5 h-3.5" /> Verträge ({contracts.length})</div>{contracts.length ? <div className="mt-2 space-y-1.5">{contracts.map((item) => <div className="text-xs flex justify-between gap-2" key={item.id}><span className="truncate">{item.bezeichnung || item.vertragspartner}</span><button className="text-red-600 hover:underline shrink-0" onClick={() => unlink(item.id)}>Lösen</button></div>)}</div> : <p className="mt-2 text-xs text-[var(--brand-muted)]">Noch kein Vertrag verknüpft.</p>}</div>
      <div className="border border-[var(--brand-border-light)] rounded-lg p-3"><div className="text-xs font-semibold text-[var(--brand-ink)] flex items-center gap-1"><BriefcaseBusiness className="w-3.5 h-3.5" /> Projekte ({projects.length})</div>{projects.length ? <div className="mt-2 space-y-1.5">{projects.map((item) => <div className="text-xs" key={item.id}><span className="block truncate">{item.name}</span><span className="text-[var(--brand-muted)]">{item.status}</span></div>)}</div> : <p className="mt-2 text-xs text-[var(--brand-muted)]">Noch keine Projekte aus diesem Kundenkontext.</p>}</div>
      <div className="border border-[var(--brand-border-light)] rounded-lg p-3"><div className="text-xs font-semibold text-[var(--brand-ink)] flex items-center gap-1"><ReceiptText className="w-3.5 h-3.5" /> Zugeordnete Kosten ({costs.length})</div>{costs.length ? <div className="mt-2 space-y-1.5">{costs.slice(0, 4).map((item) => <div className="text-xs flex justify-between gap-2" key={item.id}><span className="truncate">{item.lieferant_name || item.rechnungsnummer || "Eingangsrechnung"}</span><span>{money(item.betrag_brutto, item.waehrung || "EUR")}</span></div>)}</div> : <p className="mt-2 text-xs text-[var(--brand-muted)]">Noch keine Kundenzuordnung bei Eingangsrechnungen.</p>}</div>
    </div>
    <div className="mt-3 flex flex-col sm:flex-row gap-2"><select className="h-9 flex-1 rounded border border-[var(--brand-border)] px-2 text-sm" value={contractId} onChange={(e) => setContractId(e.target.value)}><option value="">– vorhandenen Vertrag diesem Kunden zuordnen –</option>{unlinked.map((item) => <option key={item.id} value={item.id}>{item.bezeichnung || item.vertragspartner}</option>)}</select><Button size="sm" onClick={link} disabled={!contractId}><Link2 className="w-3.5 h-3.5 mr-1" /> Vertrag verknüpfen</Button></div>
  </Card>;
}
